import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { completeEmailSignIn } from '../services/firebaseAuth';
import { useAuth } from '../contexts/AuthContext';

export default function FinishSignInPage() {
  const { currentUser } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (currentUser && !loading && !error) {
      window.location.href = '/';
    }
  }, [currentUser, loading, error]);

  useEffect(() => {
    const handleEmailLink = async () => {
      try {
        const result = await completeEmailSignIn(window.location.href);
        if (result.error) {
          // Check if it's the referer-blocked error
          if ((result.error.includes('referer') || result.error.includes('blocked')) && retryCountRef.current < 1) {
            // Retry once after a short delay
            console.log('Retrying email sign-in after referer error...');
            retryCountRef.current = 1;
            setIsRetrying(true);
            setTimeout(() => {
              setIsRetrying(false);
              handleEmailLink();
            }, 1500);
            return;
          }
          setError(result.error);
          setLoading(false);
        } else {
          // Success - set loading false, redirect useEffect will handle navigation
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to complete sign-in');
        setLoading(false);
      }
    };

    handleEmailLink();
  }, []); // Empty dependency array - runs only once on mount

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {loading ? (
          <>
            <Title>Completing Sign-In...</Title>
            {isRetrying && <RetryMessage>Retrying connection...</RetryMessage>}
            <Spinner />
          </>
        ) : error ? (
          <>
            <Title>Sign-In Failed</Title>
            <ErrorMessage>{error}</ErrorMessage>
            <InfoMessage>
              If you just clicked the link, try clicking it again from your email.
              Sometimes it takes two attempts for the authentication to complete.
            </InfoMessage>
            <BackButton onClick={() => window.location.href = '/'}>
              Back to Login
            </BackButton>
          </>
        ) : (
          <>
            <Title>Sign-In Successful!</Title>
            <SuccessMessage>Redirecting you now...</SuccessMessage>
            <Spinner />
          </>
        )}
      </Card>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: calc(24px + env(safe-area-inset-top, 0px)) calc(24px + env(safe-area-inset-right, 0px)) 24px calc(24px + env(safe-area-inset-left, 0px));
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 50%);
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 48px 32px;
  max-width: 450px;
  width: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Title = styled.h2`
  margin: 0 0 24px;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text};
`;

const Spinner = styled.div`
  margin: 24px auto;
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fcc;
  font-size: 14px;
  margin-bottom: 24px;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  margin-bottom: 24px;
`;

const RetryMessage = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin-bottom: 16px;
  font-style: italic;
`;

const InfoMessage = styled.div`
  background-color: #fffbeb;
  color: #92400e;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fde68a;
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.98);
  }
`;
